/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC } from 'react';

import { type UcDeptTreeNode } from '@coze-foundation/account-adapter';
import { I18n } from '@coze-arch/i18n';
import { Checkbox, CheckboxGroup, Tag } from '@coze-arch/coze-design';

import {
  PERMISSION_SPECIFIC_ORG,
  PUBLISH_SCOPE_OPTIONS,
  getPublishScopeStoreTipKey,
  isPublishScopeType,
  type PublishScopeType,
} from '../../constants/permission-scope';

import styles from './index.module.less';

export interface PublishScopeValue {
  scopeType: PublishScopeType;
  scopeTypeArray: string[];
  orgs: UcDeptTreeNode[];
  appointData: string[];
}

export interface PublishScopeSelectorProps {
  value: PublishScopeValue;
  onChange: (value: PublishScopeValue) => void;
  onOpenDept: () => void;
}

export const PublishScopeSelector: FC<PublishScopeSelectorProps> = ({
  value,
  onChange,
  onOpenDept,
}) => {
  const onScopeTypeChange = (checked: string[]) => {
    if (checked.length === 0) {
      onChange({
        ...value,
        scopeType: '',
        scopeTypeArray: [],
      });
      return;
    }
    const last = String(checked[checked.length - 1]);
    if (!isPublishScopeType(last)) {
      return;
    }
    onChange({
      ...value,
      scopeType: last,
      scopeTypeArray: [last],
    });
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>
        {I18n.t('abr_publish_scope_section_title')}
      </div>
      <div className={styles.scopeTip}>{I18n.t('abr_publish_scope_tip')}</div>
      <CheckboxGroup
        value={value.scopeTypeArray}
        onChange={vals => onScopeTypeChange(vals as string[])}
      >
        <div className={styles.scopeRow}>
          {PUBLISH_SCOPE_OPTIONS.filter(
            opt => opt.value !== PERMISSION_SPECIFIC_ORG,
          ).map(opt => (
            <Checkbox key={opt.value} value={opt.value}>
              {I18n.t(opt.labelKey)}
            </Checkbox>
          ))}
        </div>
        <div className={styles.scopeRowSpecific}>
          <Checkbox value={PERMISSION_SPECIFIC_ORG}>
            {I18n.t('abr_publish_scope_specific_org')}
          </Checkbox>
          {value.scopeType === PERMISSION_SPECIFIC_ORG ? (
            <button
              type="button"
              className={styles.settingBtnActive}
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                onOpenDept();
              }}
            >
              {I18n.t('abr_publish_scope_settings')}
            </button>
          ) : null}
        </div>
      </CheckboxGroup>
      {value.scopeType === PERMISSION_SPECIFIC_ORG &&
      value.appointData.length > 0 ? (
        <div className={styles.scopeSelectedOrgs}>
          <span className={styles.scopeSelectedLabel}>
            {I18n.t('abr_publish_scope_selected_count', {
              count: value.appointData.length,
            })}
          </span>
          <div className={styles.scopeSelectedTags}>
            {value.orgs.map(org => (
              <Tag key={org.departmentId} size="small" color="blue">
                {org.departmentName}
              </Tag>
            ))}
          </div>
        </div>
      ) : null}
      <div className={`${styles.tip} ${styles.scopeStoreTip}`}>
        {I18n.t(getPublishScopeStoreTipKey(value.scopeType))}
      </div>
    </div>
  );
};

export const createDefaultPublishScopeValue = (): PublishScopeValue => ({
  scopeType: '0',
  scopeTypeArray: ['0'],
  orgs: [],
  appointData: [],
});

export function publishScopeValueFromConfig(
  scopeType: string,
  scopeDeptIds: string[],
  orgs: UcDeptTreeNode[] = [],
): PublishScopeValue {
  const normalized =
    isPublishScopeType(scopeType) && scopeType !== '' ? scopeType : '0';
  return {
    scopeType: normalized,
    scopeTypeArray: [normalized],
    orgs,
    appointData: [...scopeDeptIds],
  };
}
